import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as CloseIcon } from "./../../assets/icons/close.svg";
import { ReactComponent as CheckIcon } from "./../..//assets/icons/check.svg";
import { AiNotebooksUrl } from "../../utils/urls";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AiNotebookCreateSchema } from "../../utils/validations";
import {
  selectInstancesRegions,
  selectInstancesRegionsLoading,
  getRegionsAsync,
} from "../../store/instances/instancesSlice";
import {
  getDBSCatalogsAsync,
  selectDBSCatalogs,
  selectDBSCatalogsLoading,
} from "../../store/database/servicesSlice";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { InstancesSskKeyEditorModal } from "../../components/instances/ssh-keys/InstancesSskKeyEditorModal";
import { Button, cn, Flex, Loading, Typography } from "djuno-design";
import useLimitations from "../../hooks/useLimitations";
import { DBSCatalog } from "../../types/database";
import {
  createAiNotebookAsync,
  getAiNotebookEditorsAsync,
  getAiNotebookFreamworksAsync,
  getAiRegionsDataAsync,
  getAiResourceFlavorsAsync,
  selectAiNotebookEditors,
  selectAiNotebookEditorsLoading,
  selectAiNotebookFreamworks,
  selectAiNotebookFreamworksLoading,
  selectAiNotebooks,
  selectAiNotebooksActionLoading,
  selectAiNotebooksLoading,
  selectAiRegionsData,
  selectAiRegionsDataLoading,
} from "../../store/ai/notebooks/notebooksSlice";
import {
  AiNotebookAttachRepoInput,
  AiNotebookAttachSshInput,
  AiNotebookConfigInput,
  AiNotebookEditorInput,
  AiNotebookFlavorInput,
  AiNotebookFreamworkInput,
  AiNotebookRegionInput,
} from "../../components/ai-notebooks/create/components";
import { getInstancesSshKeysAsync } from "../../store/instances/instancesSSHkeysSlice";
import { datacenterDetails } from "../../utils/countries";

const createSteps = [
  "configuration",
  "codeEditor",
  "freamwork",
  "region",
  "resources",
  "attachContainers",
  "attachSSH",
  "review",
];

type CreateStep = (typeof createSteps)[number];
export type CreateNotebookFormType = {
  notebookName: string;
  labels?: Array<{ key: string; value: string }>;
  selectedEditor: string;
  selectedFreamwork: string;
  selectedFreamworkVersion: string;
  selectedRegionDataId: string;
  selectedResourceId: string;
  selectedResourceUnit: number;
  gitRepos?: Array<{ url: string; mountPath: string; permission: string }>;
  sshKeys?: Array<{ name: string; content: string; disabled: boolean }>;
};

const AiNotebookCreatePage = () => {
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const notebooks = useAppSelector(selectAiNotebooks);
  const notebooksLoading = useAppSelector(selectAiNotebooksLoading);

  const editors = useAppSelector(selectAiNotebookEditors);
  const editorsLoading = useAppSelector(selectAiNotebookEditorsLoading);

  const freamworks = useAppSelector(selectAiNotebookFreamworks);
  const freamworksLoading = useAppSelector(selectAiNotebookFreamworksLoading);

  const regions = useAppSelector(selectInstancesRegions);
  const regionsLoading = useAppSelector(selectInstancesRegionsLoading);
  const regionsData = useAppSelector(selectAiRegionsData);
  const regionsDataLoading = useAppSelector(selectAiRegionsDataLoading);

  const catalogs = useAppSelector(selectDBSCatalogs);
  const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);

  const actionLoading = useAppSelector(selectAiNotebooksActionLoading);

  // states
  const [expanded, setExpanded] = useState<CreateStep>("configuration");
  const [filteredCatalogs, setFilteredCatalogs] = useState<Array<DBSCatalog>>(
    []
  );

  //data for create from backup
  const returnUrl = query.get("returnUrl");

  const methods = useForm<CreateNotebookFormType>({
    resolver: yupResolver(AiNotebookCreateSchema),
    mode: "all",
    defaultValues: {
      notebookName: "",
      labels: undefined,
      selectedEditor: "",
      selectedFreamwork: "",
      selectedFreamworkVersion: "",
      selectedRegionDataId: "",
      selectedResourceId: "",
      selectedResourceUnit: 1,
      gitRepos: undefined,
      sshKeys: undefined,
    },
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors, isValid, isValidating },
    trigger,
    watch,
    register,
  } = methods;

  // watch form values
  const notebookName = watch("notebookName");
  const notebookLabels = watch("labels");
  const selectedEditorId = watch("selectedEditor");
  const selectedFreamworkId = watch("selectedFreamwork");
  const selectedFreamworkVersionName = watch("selectedFreamworkVersion");
  const selectedRegionDataId = watch("selectedRegionDataId");
  const selectedResourceId = watch("selectedResourceId");
  const selectedResourceUnit = watch("selectedResourceUnit");
  const attachedGitRepos = watch("gitRepos");
  const attachedSshKeys = watch("sshKeys");

  // console.log({ selectedRegionDataId });
  useEffect(() => {
    if (isValidating) {
      trigger("labels");
      trigger("gitRepos");
      trigger("sshKeys");
    }
  }, [isValidating, trigger]);

  //get first data
  useEffect(() => {
    dispatch(getAiNotebookEditorsAsync({}));
    dispatch(getAiNotebookFreamworksAsync({}));
    dispatch(getRegionsAsync());
    dispatch(getAiRegionsDataAsync({}));
    dispatch(getInstancesSshKeysAsync());
  }, [dispatch]);

  // get resource (flavors) based on selected region
  useEffect(() => {
    if (selectedRegionDataId && selectedRegionDataId !== "")
      dispatch(getAiResourceFlavorsAsync({ region: selectedRegionDataId }));
  }, [dispatch, selectedRegionDataId]);

  // find selected code editor data
  const selectedCodeEditor = useMemo(() => {
    let _selectedImage = editors.find((e) => e.id === selectedEditorId);
    return _selectedImage;
  }, [editors, selectedEditorId]);

  // find selected freamwork data
  const selectedFreamwork = useMemo(() => {
    let _selectedImage = freamworks.find((f) => f.id === selectedFreamworkId);
    return _selectedImage;
  }, [freamworks, selectedFreamworkId]);

  // find selected region data
  const selectedRegion = useMemo(() => {
    return regions.find((r) => r.datacenterLocation === selectedRegionDataId);
  }, [regions, selectedRegionDataId]);

  // find selected datacenter
  const selectedDatacenter = useMemo(() => {
    return datacenterDetails.find((dc) => dc.location === selectedRegionDataId);
  }, [selectedRegionDataId]);

  // filter addons by selected engine and period
  // useEffect(() => {
  //   if (catalogs) {
  //     const filtered = catalogs.filter(
  //       (catalog) => catalog.product === "publiccloud-instance"
  //     );
  //     setFilteredCatalogs(filtered);
  //   }
  // }, [catalogs, selectedBillingPeriod, selectedFlavorName]);

  const handleEditConfigSelection = () => {
    setExpanded("configuration");
  };

  const handleEditCodeEditorSelection = () => {
    setExpanded("codeEditor");
  };

  const handleEditFreamworkSelection = () => {
    setExpanded("freamwork");
  };

  const handleEditRegionSelection = () => {
    setExpanded("region");
  };

  const handleEditResourcesSelection = () => {
    setExpanded("resources");
  };

  const handleEditAttachContainersSelection = () => {
    setExpanded("attachContainers");
  };

  const handleEditAttachSshSelection = () => {
    setExpanded("attachSSH");
  };

  const handleSubmitForm = (data: any) => {
    console.log(data);
    // const flavorId = isFlexible ? selectedFlexFlavor?.id : selectedFlavor?.id;
    // if (flavorId && !actionLoading) {
    //   const number = data.numberOfInstances;
    //   const apiData = {
    //     autobackup: data.autobackup,
    //     flavorId,
    //     imageId: instanceSnapshot ? instanceSnapshot.id : data.selectedImageId,
    //     monthlyBilling: billingPeriod === "monthly",
    //     name: data.instanceName,
    //     region: data.selectedRegion,
    //     sshKeyId: data.selectedSshKeyId,
    //   };
    // if (!number || number === 1) {
    // dispatch(createAiNotebookAsync({ data: apiData })).then((action) => {
    //   if (action.type === "ai-notebooks/create/fulfilled") {
    //     navigate(returnUrl || AiNotebooksUrl);
    //   }
    // });
    // } else {
    //   const multipleApiData = { number: data.numberOfInstances, ...apiData };
    //   dispatch(createMultipleInstanceAsync({ data: multipleApiData })).then(
    //     (action) => {
    //       if (action.type === "instances/create-multiple/fulfilled") {
    //         navigate(returnUrl || AiNotebooksUrl);
    //       }
    //     }
    //   );
    // }
    // }
  };

  const { numberOfAiNotebooksLimit, numberOfAiNotebooksLimitChecker } =
    useLimitations({
      cases: ["NumberOfAiNotebooks"],
    });

  const isExceedNumberOfAiNotebooks = useMemo(() => {
    return numberOfAiNotebooksLimitChecker(notebooks.length);
  }, [notebooks, numberOfAiNotebooksLimitChecker]);

  return (
    <>
      <div className="flex items-center justify-between h-16 px-6 sticky top-0 z-20 bg-white dark:bg-dark-1 border-b dark:border-dark-2">
        <div className="items-center justify-between flex flex-1 transition duration-150">
          <Typography.Text className="font-medium">
            Create a notebook
          </Typography.Text>
        </div>
        <div className="">
          <Button
            uiType="light"
            uiSize="small"
            onClick={() => navigate(returnUrl || AiNotebooksUrl)}
            className="group"
          >
            <CloseIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
          </Button>
        </div>
      </div>

      {catalogsLoading && (
        <Flex
          items="center"
          justify="center"
          className="h-full w-full min-h-[calc(100vh-128px)]"
        >
          <Loading borderSize={2} />
        </Flex>
      )}
      {!catalogsLoading && (
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className="mt-10 w-full px-6 flex flex-col lg:flex-row pb-24 gap-8 relative">
            <div className="w-full flex flex-col">
              {/* select configs */}
              <Accordion
                expanded={expanded === "configuration"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="configuration"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 0 ? (
                      <div className="flex flex-col gap-5 w-full">
                        <Flex
                          direction={{ default: "col", md: "row" }}
                          justify={{ default: "between" }}
                          className="gap-4 w-full"
                        >
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Notebook name: {notebookName}{" "}
                            <Typography.Text size="xs" uiType="secondary">
                              ({notebookLabels?.length || 0} labels)
                            </Typography.Text>
                          </Typography.Text>
                          <Typography.Link
                            className="!text-sm"
                            onClick={handleEditConfigSelection}
                          >
                            edit this step
                          </Typography.Link>
                        </Flex>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "configuration",
                          }
                        )}
                      >
                        1. Configure your notebook
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiNotebookConfigInput
                      control={control}
                      register={register}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      !notebookName ||
                      notebookName === "" ||
                      errors.notebookName !== undefined
                    }
                    onClick={() => setExpanded("codeEditor")}
                    id="4th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select code editor */}
              <Accordion
                expanded={expanded === "codeEditor"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="codeEditor"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 1 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 flex-shrink-0 text-primary-500" />
                          Code editor chosen: {selectedCodeEditor?.name}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditCodeEditorSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "codeEditor",
                          }
                        )}
                      >
                        2. Code editors
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiNotebookEditorInput
                      control={control}
                      errorMessage={errors.selectedEditor?.message}
                      editors={editors}
                      loading={editorsLoading}
                    />
                  </div>
                  <Button
                    id="1th-next-button"
                    disabled={selectedEditorId === ""}
                    onClick={() => setExpanded("freamwork")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select freamwork */}
              <Accordion
                expanded={expanded === "freamwork"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="freamwork"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 2 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 flex-shrink-0 text-primary-500" />
                          Framework selected: {selectedFreamwork?.name} -{" "}
                          {selectedFreamworkVersionName}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditFreamworkSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "freamwork",
                          }
                        )}
                      >
                        3. Available frameworks
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiNotebookFreamworkInput
                      control={control}
                      freamworks={freamworks}
                      errorMessage={
                        errors.selectedFreamwork?.message ||
                        errors.selectedFreamworkVersion?.message
                      }
                      loading={freamworksLoading}
                    />
                  </div>
                  <Button
                    id="1th-next-button"
                    disabled={
                      selectedFreamworkId === "" ||
                      selectedFreamworkVersionName === ""
                    }
                    onClick={() => setExpanded("region")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select region */}
              <Accordion
                expanded={expanded === "region"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="region"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 3 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Location selected: {selectedRegionDataId}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditRegionSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "region",
                          }
                        )}
                      >
                        4. Datacenter location
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiNotebookRegionInput
                      regions={regions}
                      regionsData={regionsData}
                      control={control}
                      loading={regionsLoading || regionsDataLoading}
                      errorMessage={errors.selectedRegionDataId?.message}
                      // disabled={!!instanceSnapshot}
                    />
                  </div>
                  <Button
                    id="2th-next-button"
                    disabled={selectedRegionDataId === ""}
                    onClick={() => setExpanded("resources")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select resources */}
              <Accordion
                expanded={expanded === "resources"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="resources"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 4 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Resource selected: {selectedResourceId} - x
                          {selectedResourceUnit}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditResourcesSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "resources",
                          }
                        )}
                      >
                        5. Resources
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiNotebookFlavorInput
                      control={control}
                      errorMessage={errors.selectedResourceId?.message}
                    />
                  </div>

                  <Button
                    disabled={!selectedResourceId || selectedResourceId === ""}
                    onClick={() => setExpanded("attachContainers")}
                    id="3th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* attach Containers */}
              <Accordion
                expanded={expanded === "attachContainers"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="attachContainers"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 5 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Containers attached: {attachedGitRepos?.length ||
                            0}{" "}
                          volumes attached
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditAttachContainersSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500":
                              expanded === "attachContainers",
                          }
                        )}
                      >
                        6. Attach a container or a Git repository
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiNotebookAttachRepoInput
                      control={control}
                      register={register}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      errors.gitRepos &&
                      errors.gitRepos.length !== undefined &&
                      errors.gitRepos.length > 0
                    }
                    onClick={() => setExpanded("attachSSH")}
                    id="attachContainers"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* attach SSH */}
              <Accordion
                expanded={expanded === "attachSSH"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="attachSSH"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 6 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Attached SSH public keys:{" "}
                          {attachedSshKeys?.length || 0}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditAttachSshSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "attachSSH",
                          }
                        )}
                      >
                        7. Attach SSH public keys
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiNotebookAttachSshInput
                      control={control}
                      register={register}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      errors.sshKeys &&
                      errors.sshKeys.length !== undefined &&
                      errors.sshKeys.length > 0
                    }
                    onClick={() => setExpanded("review")}
                    id="attachSSH"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* review */}
              <Accordion
                expanded={expanded === "review"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="review"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 7 ? (
                      <div className="flex flex-col gap-5 w-full">
                        <div className="flex w-full items-center justify-between">
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Review
                          </Typography.Text>
                        </div>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500":
                              expanded === "billingSelection",
                          }
                        )}
                      >
                        8. Review
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <Flex direction="col" className="w-full gap-5 px-5">
                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Code editor</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedCodeEditor?.name}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Framework</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedFreamwork?.name} -{" "}
                          {selectedFreamworkVersionName}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Region</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedRegion ? (
                            <>
                              {selectedRegion.countryCode.toUpperCase()} -{" "}
                              {selectedDatacenter?.title} ({selectedRegion.name}
                              )
                            </>
                          ) : (
                            <> {selectedRegionDataId}</>
                          )}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Price</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          €??? resource/hour
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">
                          Template chosen
                        </Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedResourceId} - x{selectedResourceUnit}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  </div>

                  <Flex>
                    <Button
                      type="submit"
                      disabled={!isValid || isExceedNumberOfAiNotebooks}
                      uiType="primary"
                      loading={actionLoading}
                      tooltip={{
                        content: isExceedNumberOfAiNotebooks
                          ? numberOfAiNotebooksLimit?.LimitationMessage
                          : "",
                        className: "!text-sm",
                      }}
                    >
                      Create a Notebook
                    </Button>
                  </Flex>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </form>
      )}
      <InstancesSskKeyEditorModal />
    </>
  );
};

export default AiNotebookCreatePage;
